import React, { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

declare global {
  interface Window {
    google: any;
  }
}

interface GoogleOneTapProps {
  clientId?: string;
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ 
  clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Pages where One Tap should NOT show
  const excludedPages = ['/login', '/signup', '/reset-password'];
  const isExcludedPage = excludedPages.includes(location.pathname);

  const handleCredentialResponse = useCallback(async (response: any) => {
    try {
      // console.log('Google One Tap response received');
      
      // Sign in with Google using Supabase
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential,
      });

      if (error) {
        console.error('Error signing in with Google:', error);
        return;
      }

      if (data.user) {
        // console.log('Successfully logged in:', data.user.email);
        
        // Redirect to dashboard after successful login
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error during Google One Tap login:', error);
    }
  }, [navigate]);

  useEffect(() => {
    // Exit early if user is already logged in - don't even check Google library
    if (user) {
      return;
    }

    // Debug environment variable (commented out to reduce console noise)
    // console.log('Environment variable check:', {
    //   REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    //   clientIdProp: clientId,
    //   allEnvVars: Object.keys(process.env).filter(key => key.includes('GOOGLE'))
    // });

    // Only show One Tap if:
    // 1. User is not logged in (already checked above)
    // 2. We're NOT on an excluded page (login, signup, reset-password)
    // 3. Google library is loaded
    // 4. Client ID is available
    if (isExcludedPage || !window.google || !clientId) {
      // console.log('Google One Tap not showing:', {
      //   hasUser: !!user,
      //   isExcludedPage,
      //   hasGoogleLibrary: !!window.google,
      //   hasClientId: !!clientId,
      //   clientIdValue: clientId,
      //   currentPath: location.pathname
      // });
      return;
    }

    // console.log('Initializing Google One Tap on page:', location.pathname);
    // console.log('Client ID being used:', clientId);

    try {
      // Initialize Google One Tap
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: true, // Automatically sign in if user has previously signed in
        cancel_on_tap_outside: false, // Don't cancel if user clicks outside
      });

      // Display the One Tap prompt
      window.google.accounts.id.prompt();

      // Set the position of the One Tap prompt to top-right
      const style = document.createElement('style');
      style.textContent = `
        /* Position Google One Tap prompt in top-right corner */
        #credential_picker_container {
          position: fixed !important;
          top: 20px !important;
          right: 20px !important;
          left: auto !important;
          z-index: 9999 !important;
        }
        
        /* Ensure it's above other elements */
        iframe[src*="accounts.google.com"] {
          z-index: 9999 !important;
        }
      `;
      document.head.appendChild(style);

      // Cleanup function
      return () => {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
        // Cancel any pending Google One Tap prompts when component unmounts
        if (window.google?.accounts?.id) {
          window.google.accounts.id.cancel();
        }
      };
    } catch (error) {
      console.error('Error initializing Google One Tap:', error);
    }
  }, [user, isExcludedPage, clientId, handleCredentialResponse]);

  // Don't render anything - this component just handles the One Tap logic
  return null;
};

export default GoogleOneTap;